/**
 * AB Test Effects
 * NgRx effects for handling side effects in AB test state management
 *
 * Note: Variant assignment actions exist but don't trigger API calls -
 * assignments are calculated and stored client-side only.
 */

import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap, tap } from 'rxjs/operators';
import { AB_TEST_USER_JOURNEY_TRACKER } from '../services';
import { AB_TEST_API_SERVICE } from '../tokens';
import { ABTestConfigActions, EventTrackingActions } from './ab-test.actions';

@Injectable()
export class ABTestEffects {
  private actions$ = inject(Actions);
  private abTestApiService = inject(AB_TEST_API_SERVICE);
  private userJourneyTrackerService = inject(AB_TEST_USER_JOURNEY_TRACKER);

  // Load active test definitions from backend (this is the only backend call needed)
  loadActiveTests$ = createEffect(() =>
    this.actions$.pipe(
      ofType(ABTestConfigActions.loadActiveTests),
      switchMap(() =>
        this.abTestApiService.getActiveTests().pipe(
          map((tests) => ABTestConfigActions.loadActiveTestsSuccess({ tests })),
          catchError((error) => of(ABTestConfigActions.loadActiveTestsFailure({ error: this.getErrorMessage(error) })))
        )
      )
    )
  );

  trackEduContentInteractionEvent$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(EventTrackingActions.trackEduContentInteraction),
        tap((action) =>
          this.userJourneyTrackerService.trackUserInteraction(action.action, action.elementId, action.properties)
        )
      ),
    { dispatch: false }
  );

  // Note: VariantAssignmentActions are handled purely client-side by VariantAssignmentService
  // No effects needed - assignments are calculated based on userId + testId and stored in localStorage

  private getErrorMessage(error: { message: string } | string): string {
    if (typeof error === 'string') {
      return error;
    }
    if (error && typeof error === 'object' && error.message !== undefined) {
      return error.message;
    }
    if (error && typeof error === 'object' && error.message === undefined) {
      return 'undefined';
    }
    return String(error);
  }
}
